import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProbeProvider } from '@/probe/ProbeContext';
import { ProgrammerProvider } from '@/hid/ProgrammerContext';
import ModeSelector from './connect/ModeSelector';
import type { DeviceMode } from './connect/ModeSelector';
import ProbeSelector from './connect/ProbeSelector';
import TargetInfoView from './connect/TargetInfo';
import ProgrammerSelector from './connect/ProgrammerSelector';
import ChipSelector from './connect/ChipSelector';
import TargetInfo8051 from './connect/TargetInfo8051';
import ProductPicker from './firmware/ProductPicker';
import VersionPicker from './firmware/VersionPicker';
import BuildInfoCard from './firmware/BuildInfoCard';
import MapFileViewer from './firmware/MapFileViewer';
import ListingViewer from './firmware/ListingViewer';
import FlashProgressView from './flash/FlashProgress';
import VerifyResult from './flash/VerifyResult';
import FlashControls from './flash/FlashControls';
import FlashControls8051 from './flash/FlashControls8051';
import ReadbackPanel from './readback/ReadbackPanel';
import ReadbackPanel8051 from './readback/ReadbackPanel8051';
import OptionBytesView from './readback/OptionBytesView';
import ConfigBytesView from './readback/ConfigBytesView';
import FaultDiagnosis from './readback/FaultDiagnosis';
import HexDumpView from './readback/HexDumpView';
import CompareView from './readback/CompareView';
import RegisterView from './debug/RegisterView';
import MemoryView from './debug/MemoryView';
import ConsoleLog from './debug/ConsoleLog';
import BatchMode from './production/BatchMode';
import FlashReport from './production/FlashReport';

type TabId = 'connect' | 'flash' | 'readback' | 'production';

const TABS: TabId[] = ['connect', 'flash', 'readback', 'production'];

const WebFlashPage: React.FC = () => {
  const { t } = useTranslation('webflash');
  const [activeTab, setActiveTab] = useState<TabId>('connect');
  const [deviceMode, setDeviceMode] = useState<DeviceMode>('arm');

  return (
    <ProbeProvider>
      <ProgrammerProvider>
        <div className="space-y-6">
          {/* Header */}
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('subtitle')}</p>
          </div>

          {/* Mode Selector */}
          <ModeSelector mode={deviceMode} onModeChange={setDeviceMode} />

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  {t(`tabs.${tab}`)}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'connect' && deviceMode === 'arm' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ProbeSelector />
                  <TargetInfoView />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ProductPicker />
                  <VersionPicker />
                </div>
                <BuildInfoCard />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <MapFileViewer />
                  <ListingViewer />
                </div>
                {/* Debug section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <RegisterView />
                  <MemoryView />
                  <ConsoleLog />
                </div>
              </div>
            )}

            {activeTab === 'connect' && deviceMode === '8051' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ProgrammerSelector />
                  <ChipSelector />
                </div>
                <TargetInfo8051 />
                <ConsoleLog />
              </div>
            )}

            {activeTab === 'flash' && deviceMode === 'arm' && (
              <div className="space-y-6">
                <FlashControls />
                <FlashProgressView />
                <VerifyResult />
              </div>
            )}

            {activeTab === 'flash' && deviceMode === '8051' && (
              <div className="space-y-6">
                <FlashControls8051 />
                <FlashProgressView />
              </div>
            )}

            {activeTab === 'readback' && deviceMode === 'arm' && (
              <div className="space-y-6">
                <ReadbackPanel />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <OptionBytesView />
                  <FaultDiagnosis />
                </div>
                <HexDumpView />
                <CompareView />
              </div>
            )}

            {activeTab === 'readback' && deviceMode === '8051' && (
              <div className="space-y-6">
                <ReadbackPanel8051 />
                <ConfigBytesView />
                <HexDumpView />
                <CompareView />
              </div>
            )}

            {activeTab === 'production' && (
              <div className="space-y-6">
                <BatchMode />
                <FlashReport />
              </div>
            )}
          </div>
        </div>
      </ProgrammerProvider>
    </ProbeProvider>
  );
};

export default WebFlashPage;
